export type NodeType =
  | 'input'
  | 'output'
  | 'splitter'
  | 'merger'
  | 'diverger'
  | 'processor'
  | 'gate'
  | 'waypoint';

export const NODE_SHAPES = [
  'circle',
  'square',
  'rounded_square',
  'rectangle',
  'triangle',
  'diamond',
  'hexagon',
  'pentagon',
  'octagon',
  'star',
  'ellipse',
  'cross',
] as const;

export type NodeShapeName = (typeof NODE_SHAPES)[number];

export interface NodeTypeAppearance {
  color?: string;
  radius?: number;
  shape?: NodeShapeName;
}

export type UnitSystem = 'metric_m' | 'metric_cm' | 'imperial_ft';

export interface CalibrationPoint {
  x: number;
  y: number;
}

export interface CalibrationData {
  p1: CalibrationPoint;
  p2: CalibrationPoint;
  distance: number;
  unit?: UnitSystem;
}

export interface ProjectNode {
  id: string;
  type: NodeType;
  /** Legacy/imported projects may store the type under `node_type`. */
  node_type?: NodeType;
  name: string;
  group?: string;
  x: number;
  y: number;
  color?: string;
  radius?: number;
  enabled?: boolean;
  notes?: string;
  display_label?: boolean;
  show_in_simulation_controls?: boolean;
  /** Type-specific fields (products_per_minute, processor_rules, etc.) */
  [key: string]: unknown;
}

export interface RouteWaypoint {
  x: number;
  y: number;
}

export interface ProjectRoute {
  id: string;
  name: string;
  from_node_id: string;
  to_node_id: string;
  speed_m_per_min: number;
  length_meters?: number;
  waypoints?: RouteWaypoint[];
  color?: string;
  width?: number;
  output_index?: number;
  /** Max discrete items on belt (0 = unlimited). */
  max_items_in_transit?: number;
  /** Max total mass on belt in kg (0 = unlimited). */
  max_load_kg?: number;
}

export interface ProjectSettings {
  pixels_per_meter?: number;
  unit_system?: UnitSystem;
  grid_size?: number;
  snap_to_grid?: boolean;
  default_route_speed_m_per_min?: number;
  default_route_color?: string;
  default_route_width?: number;
  layout_opacity?: number;
  calibration_points?: CalibrationData;
  /** Canvas node display scale (Ctrl+scroll multiplier). Default 1.0. */
  node_display_scale?: number;
  /** Global per-type node color, size, and shape overrides. */
  node_type_appearance?: Partial<Record<NodeType, NodeTypeAppearance>>;
}

export interface NodeGroup {
  id: string;
  name: string;
  node_ids: string[];
}

export interface ProjectDocument {
  schema_version?: string;
  name: string;
  description?: string;
  layout_width: number;
  layout_height: number;
  layout_image_path?: string;
  layout_image_id?: string;
  settings?: ProjectSettings;
  nodes: ProjectNode[];
  routes: ProjectRoute[];
  node_groups?: NodeGroup[];
  custom_buttons?: unknown[];
  scenarios?: import('./scenario').ScenarioPreset[];
}

export interface ProjectSummary {
  id: string;
  name: string;
  description: string;
  archived: boolean;
  version_number: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectRecord extends ProjectSummary {
  document: ProjectDocument;
  permission?: ProjectPermissionLevel;
}

export type ProjectPermissionLevel = 'view' | 'run' | 'edit';

export interface HealthResponse {
  status: string;
  version: string;
  git_commit?: string;
}

export interface ApiError {
  error: string;
  issues?: import('./validation').ValidationIssue[];
  validation?: import('./validation').ValidationReport;
}

export interface UploadResponse {
  id: string;
  url: string;
  filename: string;
  content_type?: string;
  size_bytes: number;
}
